import { inject, Injectable } from '@angular/core';
import { Observable, map, switchMap } from 'rxjs';
import { Api, ApiConfig, ObjectData, Project } from 'bimplus-websdk';
import { BIMPLUS_API, BIMPLUS_API_CONFIG } from '@web-portal/domains/shared/bimplus-core';
import { OCP_PROJECT_TYPE } from '../../../../constants';
import {
  CreateProjectParams,
  CreateTaskPayload,
  GetProjectsParams,
  ProjectObjectAttributes,
  ProjectThumbnailUploadResponse,
  RenameProjectPayload,
  TaskShortInfoCategory,
} from './projects-adapter.interface';
import type { DtoTask, GetTasksParams } from '@web-portal/domains/shared/domain-models';

/**
 * ProjectsAdapterService - Adapter over bimplus-websdk Projects API.
 */
@Injectable({
  providedIn: 'root',
})
export class ProjectsAdapterService {
  private readonly api: Api = inject(BIMPLUS_API);
  private readonly apiConfig: ApiConfig = inject(BIMPLUS_API_CONFIG);

  private withProjectType(additionalParams?: Record<string, string>): Record<string, string> {
    return {
      ...additionalParams,
      projectType: OCP_PROJECT_TYPE,
    };
  }

  /**
   * Extracts the model count for a project.
   *
   * The SDK's Project type omits the `modelCount` field even though the API returns it.
   *
   * @param project - The project object from the API.
   * @returns The model count, or undefined if not present.
   */
  extractModelCount(project: Project): number | undefined {
    return (project as Project & { modelCount?: number }).modelCount;
  }

  /**
   * Retrieves all projects for the current user.
   *
   * Returns a list of projects that the current user has access to.
   * Can be filtered by team, disciplines, and recycle bin status.
   *
   * @param params - Optional parameters for the request.
   * @returns Observable of Project array.
   */
  getProjects(params?: GetProjectsParams): Observable<Project[]> {
    return this.api.rxRequest(
      this.api.projects.get(
        undefined,
        params?.noDisciplines,
        params?.teamSlug,
        params?.alsoRecycleBin,
        this.withProjectType(params?.additionalParams)
      )
    ) as Observable<Project[]>;
  }

  /**
   * Retrieves a specific project by ID.
   *
   * @param projectId - The ID of the project to retrieve.
   * @param params - Optional parameters for the request.
   * @returns Observable of single Project.
   */
  getProjectById(
    projectId: string,
    params?: Omit<GetProjectsParams, 'teamSlug' | 'alsoRecycleBin'>
  ): Observable<Project> {
    return this.api.rxRequest(
      this.api.projects.get(
        projectId,
        params?.noDisciplines,
        undefined,
        undefined,
        this.withProjectType(params?.additionalParams)
      )
    ) as Observable<Project>;
  }

  /**
   * Creates a new project.
   *
   * @param params - Parameters for the new project.
   * @returns Observable of created Project.
   */
  createProject(params: CreateProjectParams): Observable<Project> {
    return this.api.rxRequest(
      (
        this.api.projects as unknown as {
          post(payload: string): Promise<Project>;
        }
      ).post(
        JSON.stringify({
          name: params.name,
          shortDescr: params.shortDescr,
          projectType: OCP_PROJECT_TYPE,
        })
      )
    );
  }

  uploadProjectThumbnail(
    projectId: string,
    file: File
  ): Observable<ProjectThumbnailUploadResponse> {
    const formData = new FormData();
    formData.append('images[0]', file);

    return this.api.rxRequest(
      this.api.projects.postThumbnail(
        projectId,
        formData
      ) as Promise<ProjectThumbnailUploadResponse>
    );
  }

  deleteProjectThumbnail(projectId: string): Observable<unknown> {
    return this.api.rxRequest(this.api.projects.deleteThumbnail(projectId));
  }

  /**
   * Renames a project. Bimplus stores the project name as the `project.name`
   * object attribute, so its attribute id is read first and then updated.
   */
  renameProject(projectId: string, name: string): Observable<ObjectData> {
    return this.api.rxRequest(this.api.objects.getAttributes(projectId, undefined, projectId)).pipe(
      map(({ attributes }) => {
        const nameAttributeId = (attributes as ProjectObjectAttributes | undefined)?.project?.name
          ?.id;
        if (!nameAttributeId) {
          throw new Error(`Missing project.name attribute id for project ${projectId}`);
        }
        const payload: RenameProjectPayload = {
          id: projectId,
          attributes: { project: { name: { id: nameAttributeId, value: name } } },
        };
        return payload;
      }),
      switchMap(payload =>
        this.api.rxRequest(this.api.objects.put(projectId, JSON.stringify(payload)))
      )
    );
  }

  /**
   * Retrieves all tasks/issues for a specific project.
   *
   * Note: The API endpoint uses 'getIssues' for legacy reasons,
   * but returns DtoTask objects (raw API response).
   *
   * @param projectId - The ID of the project to get tasks for.
   * @param params - Optional parameters for the request.
   * @returns Observable of DtoTask array.
   */
  getTasks(projectId: string, params?: GetTasksParams): Observable<DtoTask[]> {
    return this.api.rxRequest(
      (
        this.api.projects as unknown as {
          getIssues(
            projectId: string,
            filterScene?: boolean,
            addThumbnailUrl?: boolean
          ): Promise<DtoTask[]>;
        }
      ).getIssues(projectId, params?.filterScene, params?.addThumbnailUrl)
    );
  }

  /**
   * Retrieves lightweight task count summary for a project.
   *
   * Calls the shortinfo endpoint which returns category-based count breakdowns
   * without loading full task objects — much cheaper than getTasks().
   *
   * @param projectId - The ID of the project.
   * @returns Observable of TaskShortInfoCategory array.
   */
  getTasksShortInfo(projectId: string): Observable<TaskShortInfoCategory[]> {
    return this.api.rxRequest(this.api.projects.getIssuesShortInfo(projectId));
  }

  /**
   * Creates a new task/issue within a project.
   *
   * The caller is responsible for building the complete payload
   * (including author, responsible, and dueDate).
   */
  createTask(projectId: string, payload: CreateTaskPayload): Observable<DtoTask> {
    return this.api.rxRequest(this.api.issues.post(projectId, JSON.stringify(payload)));
  }
}
